import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import { ProviderItem } from '~/models/Commons ';
import { ComponentsType } from '~/models/enums/ComponentsEnum';
import DecommissionStatus from '~/models/enums/DecommissionStatus.enum';
import ChipsPropertyScripts from '~/models/scripts/ChipsPropertyScripts';
import { ContentItem } from '~/models/scripts/TypesScript';
import { LABELS } from "~/resources/Labels";
type SortConfig = {
    key: string;
    direction: string;
};


/**This function is to convert string base64 to Array Buffer */
export const base64ToArrayBuffer = (base64: string) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};

/** This function  allows downloading binary data directly in the browser. */
export const downloadBlob = (arrayBuffer: ArrayBuffer, fileName: string): void => {
    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
};

export function formatDate(dateString: string | null | undefined): string {
    
    if (dateString == null) {
        return ''; 
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        throw new Error("Invalid date format. Expected format: YYYY-MM-DD");
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export const sortScripts = (scripts: Array<any>, sortConfig: SortConfig) => {
    // Default to sorting by 'creationDate' descending if no sort key is provided.
    // Use the raw ISO value (_raw field) when available for reliable date comparison.
    if (!sortConfig.key) {
        return [...scripts].sort((a, b) => {
            const aDate = new Date(a.creationDate_raw || a.creationDate || 0).getTime();
            const bDate = new Date(b.creationDate_raw || b.creationDate || 0).getTime();
            return bDate - aDate; // Descending order
        });
    }

const getValue = (script: any, key: string) => {
  if (key === LABELS.CloudProvider) {
    return script.cloudProvider?.name ?? '';
  }

  if (key === 'resourceStatus') {
    return script.resourceStatus ?? '';
  }

  if (key === 'requesterUniqueId') {
    return script.requesterUniqueId ?? '';
  }

  return script[key] ?? '';
};

type Comparable = string | number | null | undefined;

const compareValues = (
  aValue: Comparable,
  bValue: Comparable,
  direction: string
): number => {
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return direction === LABELS.ASC
      ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
      : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return direction === LABELS.ASC ? aValue - bValue : bValue - aValue;
  }

  return 0;
};

const getDateValue = (date?: string) => new Date(date || 0).getTime();

const sortedScripts = [...scripts].sort((a, b) => {
  const aValue = getValue(a, sortConfig.key) as Comparable;
  const bValue = getValue(b, sortConfig.key) as Comparable;

  const result = compareValues(aValue, bValue, sortConfig.direction);
  if (result !== 0) return result;

  return getDateValue(b.creationDate_raw || b.creationDate) - getDateValue(a.creationDate_raw || a.creationDate);
});


return sortedScripts;
}


/** Array Property to fill chips with @param label and @param icon */
 export const chipProperty: ChipsPropertyScripts[] = [{ label: LABELS.Inactive, icon: ToggleOffOutlinedIcon }];


export function attachResourcesToScripts(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item?.script,
    provider:item.script.cloudEnvironment?.cloudProvider?.cloudProviderName,
    environment:item.script.cloudEnvironment?.environmentName,
    scriptType:item.script.scriptType?.name,
    outputTemplate:item.script.outputTemplate?.name ?? '—',
    outputTemplateObj:item.script.outputTemplate ?? null,
    manual: String(item.script.manual ?? false),
    configFiles:item.configFiles??[],
    dateAdded:formatDateTime(item.script.creationDate),
    dateAdded_raw:item.script.creationDate ?? null,
    provisionedResources: item?.provisionedResources ?? [],
  }));
}
export function attachResourcesToScriptsTypes(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item?? [],
  }));
}
export function attachResourcesToCloudEnviroments(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item,
    provider:item.cloudProvider?.cloudProviderName,
    environment:item.cloudEnvironment?.cloudProvider?.cloudProviderName,
    cloudProvisionerTemplate:item.cloudProvisionerTemplate,
    creationDate:formatDateTime(item.creationDate),
    creationDate_raw:item.creationDate ?? null,
    cloudProvisionerTemplateFile:item.cloudProvisionerTemplate?.fileName,
    outputTemplate: item.outputTemplate?.name ?? '—',
  }));

}
export function attachResourcesToComponentes(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item,
    creationDate:formatDateTime(item.creationDate),
    creationDate_raw:item.creationDate ?? null,
  }));
}
export function attachResourcesToDecomission(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item,
    provisionedDateTime:formatDateTime(item.provisionedDateTime),
    provisionedDateTime_raw:item.provisionedDateTime ?? null,
    decomissionedDate:formatDateTime(item.decomissionedDateTime),
    decomissionedDate_raw:item.decomissionedDateTime ?? null,
    outputTemplateName:item.outputTemplateName ?? 'N/A',
  }));
}
export function attachResourcesToRequesters(object: any) {
 if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item:any) => ({
   ...item,
  }));
}





export function displayEntries(list: Array<any>, limit: number, maxRows: number): Array<any> {
    // If limit is greater than the list length, return all items up to the limit
    const validLimit = limit > list.length ? list.length : limit;
    // Ensure the limit does not exceed maxRows
    const finalLimit = Math.min(validLimit, maxRows);
    return list.slice(0, finalLimit);
}

export function getProvisionedResources(status: DecommissionStatus, provisionedResources: ProvisionedResources[]): ProvisionedResources[] {
  if (status === DecommissionStatus.ACTIVATED) {
    return provisionedResources.filter(resource => resource.resourceProvisionedStatus === DecommissionStatus.ACTIVATED);
  }
  return [];
}


export function getResourceProvisionedStatus(
  provisionedResources: any[], 
  statusKey: string = 'resourceProvisionedStatus'
): string {
  if (provisionedResources.length === 0) return "No resources available";
  if (provisionedResources.length === 1) return provisionedResources[0][statusKey];

  // Get all statuses
  const statuses = provisionedResources.map(res => res[statusKey]);

  // Check if 'ACTIVATED' is present
  const activatedStatuses = statuses.filter(status => status === DecommissionStatus.ACTIVATED);

  if (activatedStatuses.length > 0) {
    return DecommissionStatus.ACTIVATED;
  }

  // Remove consecutive duplicate statuses
  const uniqueStatuses = statuses.reduce<string[]>((acc, status, index, arr) => {
    if (index === 0 || status !== arr[index - 1]) {
      acc.push(status);
    }
    return acc;
  }, []);

  // Only return unique statuses if 'ACTIVATED' is not present
  if (uniqueStatuses.length === 1) {
    return uniqueStatuses[0]; 
  }

  return `Multiple resources found: ${uniqueStatuses.join(", ")}`;
}

export function fixInvalidJSON<T = any>(input: string): T | null {
  try {
    return JSON.parse(input);
  } catch {
    try {
      const onceParsed = JSON.parse(input);

      if (typeof onceParsed === 'string') {
        return JSON.parse(onceParsed);
      }
    } catch {
      // continue
    }

    try {
      let fixed = input;

      // Replace single quotes with double quotes
      fixed = fixed.replace(/'/g, '"');

      // Remove trailing commas
      fixed = fixed.replace(/,\s*([}\]])/g, '$1');

      // Quote unquoted keys
      fixed = fixed.replace(
        /([{,]\s*)(\w+)\s*:/g,
        '$1"$2":'
      );

      return JSON.parse(fixed);
    } catch (err) {
      console.error('fixInvalidJSON: Failed to fix input JSON', err);
      return null;
    }
  }
}



export type CloudProvider = {
  id: number;
  cloudProviderName: string;
};

export function getCloudProviderName(cloudProvider: CloudProvider): string {
  return cloudProvider?.cloudProviderName;
}
export function getComponentNumber(component: keyof typeof ComponentsType): number {
  return ComponentsType[component];
}
export function extractEnvDetails(cloudEnv:any[]) {
  return cloudEnv.map(env => ({
    environmentName: env.environmentName,
    id: env.id
  }));
}
export function extractScriptTypeDetails(scriptTypes:any[]) {
  return scriptTypes.map(scriptType => ({
    name: scriptType.name,
    id: scriptType.id
  }));
}
export function extractCloudProviderList(cloudProvider: any): { cloudProviderName: string; id: string }[] {
  if (!cloudProvider) return [];

  const providerList = Array.isArray(cloudProvider) ? cloudProvider : [cloudProvider];

  return providerList
    .filter(provider => provider && typeof provider === 'object')
    .map(provider => {
      const name =
        provider.cloudProviderName ??
        provider.cloudProvider?.cloudProviderName ??
        provider.name ??
        '';

      const id = provider.id ?? provider.cloudProvider?.id ?? '';

      return { cloudProviderName: name, id };
    });
}



export function extractComponentsDetails(component:any[]) {
  return component.map(env => ({
    name:env.name,
    id: env.id
    
  }));
}
export  const getDefaultQueryParams = (type: keyof typeof ComponentsType) => {
    return {
      page: 0,
      size: 100,
      active: true,
      type: type,
    };
  };
  export function getProviders(provider: any): ProviderItem[] {
    return provider?.payload;
  }
  export function capitalizeWords(str: string): string {
    return str
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }
  export  const normalizeFillArray = (value: string): string[] => {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v); 
    }

    export const  findMatch =(value: string, list: string[]): string  =>{
  return list.find(item => item === value) ?? '';
}

export const filterVMTypes = (data: []): ContentItem[] => {
   const allowedTypes = new Set(["VM", "VM_APP"]);

  return data.filter((item:ContentItem) =>
    allowedTypes.has(item.name)
  );
};
export function formatDateTime(
  dateInput: string | Date | null | undefined,
): string {
  if (!dateInput) return "N/A";

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) return "N/A";

  const formattedDate = new Intl.DateTimeFormat("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

  const hasTime =
    dateInput instanceof Date
      ? date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0
      : /T\d{2}:\d{2}| \d{2}:\d{2}/.test(dateInput);

  if (!hasTime) {
    return formattedDate;
  }

  const formattedTime = new Intl.DateTimeFormat("pt-PT", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);

  return `${formattedDate} ${formattedTime}`;
}
export const getLang = () => localStorage.getItem("lang");

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}